// src/encounter/lib/nursing-problems.ts
// [PROD-3045] Frame v2 + planSave ONLY. The nurse's NURSING PROBLEMS as structured lines (law L10,
// structure first; R8d "Nursing care plan" → "Nursing problems"), in place of the free-text box.
//
// PROPOSALS COME ONLY FROM WHAT THE NOTE ALREADY HOLDS. Each is today's own structured finding,
// restated in the tool's own words — the tool's name, the result and the band — never a new label:
//   · Fall · CDC STEADI (7 Oct 2026 ruling: STEADI and NRS 0–10 replace Morse and Wong-Baker) — "Fall risk · CDC
//     STEADI: at risk" when a key question is YES; Pain · NRS — "Pain · NRS N/10" when N ≥ 1. From the note's free-screen
//     answers, else from the managed line Flutter writes into the Fall / Pain record;
//   · Fall (OLD records only) — the Morse total and band, read from lane C's Risk lines (`riskRecordGroups`: the same
//     enableWhen rules and the same DS scoring the Risk block shows; nothing is re-scored here). It
//     qualifies when the total sits ABOVE the first coded band (structure, not words: the first band
//     is the template's lowest).
//   · Pain — "Pain present" answered Yes (an explicit answer, never the authored default), plus the
//     pain scale and the face score when they are answered and shown.
//   · Vitals — a reading the backend flagged (D-67: High / Low / Critically … / Abnormal), stated as the
//     reading. Only when the note draws a vitals block. Nothing is recomputed from thresholds.
//   · Any other scored total the note holds (e.g. a CDC prediabetes total) — only when the template
//     codes its bands and the stored total sits above the first band. No band coded → no proposal: a
//     threshold is clinical content, and it is never invented here.
// No NANDA-I or any other licensed label, no diagnosis words, never a medical diagnosis.
//
// HOW ACCEPTED PROBLEMS ARE STORED (no server change): in the existing `nursing-problem` entry, under the
// heading line "Nursing problems from today's assessment:" (the format marker), one line per problem —
// "<problem> · from <block>". A proposal the nurse dismissed is kept as "Dismissed: <problem> · from
// <block>", so it stays dismissed after a reload. Everything else is earlier free text: it is kept
// verbatim (first) and shown read-only as "Earlier note".
// Nothing typed earlier is ever dropped by a save (L7).
//
// Pure: no React, no fetch.
import {
  bandForScore,
  fallAssessmentParts,
  painAssessmentParts,
  qrItemEnabled,
  scoreBands,
  type QItem,
  type Questionnaire,
  type VitalsBlockItem,
} from '@scripsteam/scrips-signal-ds';
import { riskRecordGroups, type RiskRecordGroup } from '../structured-note/risk-record-lines';
import { freeScreenResults, freeScreenScope, riskBridgeScope, type ManagedRiskLine } from '../risk/lib/risk-qr-bridge';
import { enabledLeaves, isAnswered, pickOptions, type AnswerMap } from '../structured-note/note-adapter';
import { QUESTIONNAIRE_STEPS, questionnaireForStep, type QuestionnaireStep } from './questionnaire-groups';
import { ABNORMAL_WORD } from './to-vitals-block-items';

/** The nursing plan entry key the problems live in (./nursing-plan.ts field id; renaming orphans text). */
export const NURSING_PROBLEM_FIELD = 'nursing-problem';

/** One nursing problem line: the finding in plain words, and the note block it came from. */
export interface NursingProblem {
  text: string;
  source: string;
}

/** The `nursing-problem` entry, read back. */
export interface NursingProblemRecord {
  accepted: NursingProblem[];
  dismissed: NursingProblem[];
  /** Earlier free text that is not in the line shape — kept verbatim, shown read-only. */
  earlier: string;
}

const FROM = ' · from ';
const DISMISSED = 'Dismissed: ';
/** Written before an accepted problem only when its own words begin with a reserved prefix (Codex round 4). */
const ACCEPTED = 'Accepted: ';
const reserved = (text: string): boolean => text.startsWith(DISMISSED) || text.startsWith(ACCEPTED);
/**
 * The explicit format marker (Codex review, P2): the lines are read as problems ONLY after this heading
 * line, so earlier free text that happens to read "… · from …" is never taken for an accepted problem
 * (and so can never be removed by a "Remove"). It reads as a plain heading wherever the entry is shown.
 */
export const PROBLEMS_HEADING = "Nursing problems from today's assessment:";
/** "<problem> · from <block>", split at the LAST " · from " (a problem's own words may hold one). The stored
 *  block name never holds "·" (`storedSource`), so the split cannot move (Codex review, rounds 1 and 2). */
const LINE = /^(.+) · from (.+)$/;

const clean = (s: string | null | undefined): string => (s ?? '').replace(/\s+/g, ' ').trim();
/** The block name as stored: no "·" (Codex review round 2, P2: a title like "Risk · from intake" would
 *  otherwise move the split). Lines match on the problem's words, never on the stored block name. */
const storedSource = (s: string): string => clean(s).replace(/·/g, '-');

/** The entry as accepted, dismissed and earlier lines. Blank → empty. Everything before the LAST heading
 *  line is earlier text, verbatim; after it, a line that is not in the shape is kept as earlier text too. */
export function parseNursingProblems(text: string | null | undefined): NursingProblemRecord {
  return parsePlanLines(text, PROBLEMS_HEADING);
}

/** The entry text for a record: earlier text first (verbatim), then the heading and the accepted and
 *  dismissed lines. No lines → the earlier text alone (no heading). */
export function encodeNursingProblems(record: NursingProblemRecord): string {
  return encodePlanLines(record, PROBLEMS_HEADING);
}

/** PROD-2947 Part 2: the same line codec under another entry's own heading (the interventions entry). */
export function parsePlanLines(text: string | null | undefined, heading: string): NursingProblemRecord {
  const record: NursingProblemRecord = { accepted: [], dismissed: [], earlier: '' };
  const all = (text ?? '').split('\n');
  const at = all.map((l) => l.trim()).lastIndexOf(heading);
  const earlier: string[] = at < 0 ? all : all.slice(0, at);
  for (const raw of at < 0 ? [] : all.slice(at + 1)) {
    const line = raw.trim();
    if (line === '') continue;
    const accepted = line.startsWith(ACCEPTED);
    const dismissed = !accepted && line.startsWith(DISMISSED);
    const m = LINE.exec(accepted ? line.slice(ACCEPTED.length) : dismissed ? line.slice(DISMISSED.length) : line);
    if (m && clean(m[1]) !== '' && clean(m[2]) !== '') {
      (dismissed ? record.dismissed : record.accepted).push({ text: clean(m[1]), source: clean(m[2]) });
    } else {
      earlier.push(raw);
    }
  }
  record.earlier = earlier.join('\n').trim();
  return record;
}

export function encodePlanLines(record: NursingProblemRecord, heading: string): string {
  const lines: string[] = [];
  if (record.earlier.trim() !== '') lines.push(record.earlier);
  if (record.accepted.length > 0 || record.dismissed.length > 0) lines.push(heading);
  for (const p of record.accepted) lines.push(`${reserved(clean(p.text)) ? ACCEPTED : ''}${clean(p.text)}${FROM}${storedSource(p.source)}`);
  for (const p of record.dismissed) lines.push(`${DISMISSED}${clean(p.text)}${FROM}${storedSource(p.source)}`);
  return lines.join('\n');
}

/** One problem = the same words, whitespace folded (Codex round 3, P2: the stored line folds it, a template
 *  label may not). */
const same = (a: NursingProblem, b: NursingProblem): boolean => clean(a.text) === clean(b.text);

/** Accept a proposal: it joins the accepted lines and leaves the dismissed ones. */
export function acceptProblem(record: NursingProblemRecord, p: NursingProblem): NursingProblemRecord {
  return {
    ...record,
    accepted: record.accepted.some((a) => same(a, p)) ? record.accepted : [...record.accepted, p],
    dismissed: record.dismissed.filter((d) => !same(d, p)),
  };
}

/** Dismiss a proposal: it is remembered as dismissed, so it is not proposed again. */
export function dismissProblem(record: NursingProblemRecord, p: NursingProblem): NursingProblemRecord {
  return {
    ...record,
    accepted: record.accepted.filter((a) => !same(a, p)),
    dismissed: record.dismissed.some((d) => same(d, p)) ? record.dismissed : [...record.dismissed, p],
  };
}

/** Remove an accepted problem. It is not marked dismissed, so a finding still present is proposed again. */
export function removeProblem(record: NursingProblemRecord, p: NursingProblem): NursingProblemRecord {
  return { ...record, accepted: record.accepted.filter((a) => !same(a, p)) };
}

/** The proposals still open: today's findings the nurse has neither accepted nor dismissed. */
export function openProposals(findings: readonly NursingProblem[], record: NursingProblemRecord): NursingProblem[] {
  return findings.filter((f) => !record.accepted.some((a) => same(a, f)) && !record.dismissed.some((d) => same(d, f)));
}

// ── Findings ─────────────────────────────────────────────────────────────────────────────────

export interface NursingFindingsInput {
  questionnaire: Questionnaire | undefined;
  values: AnswerMap;
  /** The vitals block's items (the page's `resolveVitals`, D-67 flags). Used only when the note draws a
   *  vitals block. */
  vitals?: readonly VitalsBlockItem[];
  /** PROD-2947 (lanes A + G): the frame draws the Vitals block first in Objective (D-74 — the template carries no
   *  Objective marker), so the block is on screen without a template marker. Absent → only a drawn marker counts. */
  objectiveVitals?: boolean;
  /** The block name a step's findings are "from" (the step's title in this note). */
  sourceOf: (step: QuestionnaireStep) => string;
  /** PROD-2947 · 7 Oct 2026 ruling: the managed STEADI / NRS lines Flutter writes into the Fall / Pain record, used when
   *  the template authors no STEADI / NRS screen (recorded in Flutter). */
  managedRisk?: { steadi?: ManagedRiskLine; nrs?: ManagedRiskLine };
}

/** The DS band words the Risk line shows (lane C's `BAND_WORDS`), for a coded band. */
const BAND_WORDS: Record<string, string> = { low: 'Low risk', moderate: 'Moderate risk', high: 'High risk' };

/** Above the first coded band (the template's lowest)? */
function aboveFirstBand(total: number, item: QItem): { above: boolean; words: string } {
  const bands = scoreBands(item);
  const band = bands.length > 0 ? bandForScore(total, bands) : undefined;
  if (!band) return { above: false, words: '' };
  return { above: bands.indexOf(band) > 0, words: BAND_WORDS[band.code] ?? `band ${band.range || band.code}` };
}

function answerText(item: QItem, value: AnswerMap[string]): string {
  const options = pickOptions(item);
  const one = (v: unknown) => options.find((o) => o.value === v)?.label ?? String(v);
  return Array.isArray(value) ? value.map(one).join(', ') : one(value);
}

/** The Morse total, as lane C's Risk line shows it, when it sits above the first band. */
function fallProblem(group: RiskRecordGroup, source: string): NursingProblem | null {
  const total = group.rows.find((r) => r.kind === 'total');
  if (!total || total.kind !== 'total') return null;
  const sum = Number.parseInt(total.text, 10);
  if (!Number.isFinite(sum)) return null;
  const parts = fallAssessmentParts(group.group);
  if (!parts.total || !aboveFirstBand(sum, parts.total).above) return null;
  const tool = clean(parts.morse?.text) || clean(group.group.text) || 'Fall risk';
  return { text: `${tool}: ${total.text}`, source };
}

/** "Pain present: Yes", with the scale and face score when answered and shown on the Risk lines. */
function painProblem(group: RiskRecordGroup, values: AnswerMap, source: string): NursingProblem | null {
  const parts = painAssessmentParts(group.group);
  const present = parts.present;
  if (!present || !group.rows.some((r) => r.kind === 'yes-no' && r.item.linkId === present.linkId)) return null;
  if (values[present.linkId] !== true) return null;
  const bits = [`${clean(present.text) || 'Pain present'}: Yes`];
  // The pain scale / Wong-Baker score: an old record's history rows only (7 Oct 2026 ruling).
  for (const item of [parts.scale, parts.wongBaker]) {
    if (!item || !isAnswered(values[item.linkId])) continue;
    if (!group.rows.some((r) => (r.kind === 'pick' || r.kind === 'history') && r.item.linkId === item.linkId)) continue;
    bits.push(`${clean(item.text)}: ${answerText(item, values[item.linkId])}`);
  }
  return { text: bits.join(' · '), source };
}

/** Every other scored total (a leaf carrying score bands that is not a scored option list), above its
 *  first band. Its name is its group's (the instrument's) title. */
function scoredProblems(items: readonly QItem[], values: AnswerMap, skip: ReadonlySet<string>, source: string): NursingProblem[] {
  const out: NursingProblem[] = [];
  const walk = (list: readonly QItem[], parent: QItem | undefined) => {
    for (const it of list) {
      if (skip.has(it.linkId) || !qrItemEnabled(it, values)) continue;
      if (it.type === 'group') {
        walk(it.item ?? [], it);
        continue;
      }
      // A scored option list (the pain face scale) is an answer, not a total; a leaf with no coded band has
      // no threshold, so `aboveFirstBand` never proposes it.
      if (it.type === 'choice' || it.type === 'open-choice') continue;
      const raw = values[it.linkId];
      const n = typeof raw === 'number' ? raw : typeof raw === 'string' && raw.trim() !== '' ? Number(raw) : Number.NaN;
      if (!Number.isFinite(n)) continue;
      const { above, words } = aboveFirstBand(n, it);
      if (!above) continue;
      const tool = clean(parent?.text) || clean(it.text);
      if (tool === '') continue;
      out.push({ text: `${tool}: ${n} · ${words}`, source });
    }
  };
  walk(items, undefined);
  return out;
}

/** Out-of-range words (D-67) → how the reading is stated. */
const RANGE_WORDS: Record<string, string> = {
  High: 'above range',
  Low: 'below range',
  'Critically high': 'critically above range',
  'Critically low': 'critically below range',
  [ABNORMAL_WORD]: 'flagged abnormal',
};

/** The vitals tile ids (to-vitals-block-items `TileSpec.key`) in full words. */
const VITAL_NAMES: Record<string, string> = {
  temp: 'Temperature',
  pulse: 'Pulse',
  bp: 'Blood pressure',
  respRate: 'Respiratory rate',
  o2Sat: 'Oxygen saturation',
  height: 'Height',
  weight: 'Weight',
  bmi: 'BMI',
  hipCirc: 'Hip circumference',
  waistCirc: 'Waist circumference',
  waistToHipRatio: 'Waist-to-hip ratio',
};

export const VITALS_SOURCE = 'Vitals';

/** Does the note draw a vitals block — a `vitals-block` marker whose own and every parent's enableWhen is
 *  met (Codex round 4, P2: a hidden block proposes nothing)? */
function drawsVitals(items: readonly QItem[], values: AnswerMap): boolean {
  return items.some(
    (it) => it.type === 'group' && qrItemEnabled(it, values) && (it.itemControl === 'vitals-block' || drawsVitals(it.item ?? [], values)),
  );
}

function vitalProblems(vitals: readonly VitalsBlockItem[]): NursingProblem[] {
  const out: NursingProblem[] = [];
  for (const v of vitals) {
    const words = v.badge ? RANGE_WORDS[v.badge] : undefined;
    const value = clean(v.value);
    if (!words || value === '') continue;
    const name = VITAL_NAMES[v.id] ?? clean(v.label);
    out.push({ text: `${name} ${value}${v.unit ? ` ${clean(v.unit)}` : ''}, ${words}`, source: VITALS_SOURCE });
  }
  return out;
}

/** Today's findings that qualify as a nursing problem, in note order (by step, then vitals). */
export function nursingFindings(input: NursingFindingsInput): NursingProblem[] {
  const { questionnaire, values } = input;
  if (!questionnaire) return [];
  const out: NursingProblem[] = [];
  // The free screens (7 Oct 2026 ruling: CDC STEADI and NRS 0–10 replace Morse and Wong-Baker), in the tool's own name,
  // then the result, then the block: "Fall risk · CDC STEADI: at risk", "Pain · NRS 6/10". From the note's own answers
  // (the block they sit in), else from the managed line Flutter wrote into the Fall / Pain record (the Risk block).
  const authored = freeScreenScope(questionnaire.item);
  const steadiProblem = (source: string): NursingProblem => ({ text: 'Fall risk · CDC STEADI: at risk', source });
  const nrsProblem = (n: number, source: string): NursingProblem | undefined =>
    Number.isInteger(n) && n >= 1 && n <= 10 ? { text: `Pain · NRS ${n}/10`, source } : undefined;
  for (const step of QUESTIONNAIRE_STEPS) {
    const items = questionnaireForStep(questionnaire, step).item;
    if (items.length === 0) continue;
    const source = input.sourceOf(step);
    const groups = riskRecordGroups(items, values);
    for (const g of groups) {
      const p = g.kind === 'fall' ? fallProblem(g, source) : painProblem(g, values, source);
      if (p) out.push(p);
    }
    out.push(...scoredProblems(items, values, riskBridgeScope(items).linkIds, source));
    // Only the screens the template shows now: a screen its enableWhen hides keeps old answers that are no longer findings (Codex).
    const free = freeScreenResults(enabledLeaves(items, values), values);
    if (free.atRisk === true) out.push(steadiProblem(source));
    if (free.nrs !== undefined) {
      const p = nrsProblem(free.nrs, source);
      if (p) out.push(p);
    }
  }
  // The managed line stands in only for a screen the template does not author (as in the Risk block, G7): where the
  // template has the screen, the note's own (shown) answers are the finding.
  const managed = input.managedRisk;
  if (Object.keys(authored.steadi).length === 0 && managed?.steadi?.atRisk === true) out.push(steadiProblem(input.sourceOf('risk')));
  if (!authored.nrs && managed?.nrs?.score !== undefined) {
    const p = nrsProblem(managed.nrs.score, input.sourceOf('risk'));
    if (p) out.push(p);
  }
  if (input.vitals && (input.objectiveVitals || drawsVitals(questionnaire.item, values))) out.push(...vitalProblems(input.vitals));
  // One line per finding (L5): the same words from two places show once.
  return out.filter((p, i) => out.findIndex((q) => q.text === p.text) === i);
}
